import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { toast } from 'react-hot-toast';
import {
  BookOpen,
  Search,
  Plus,
  Edit,
  Trash2,
  Clock,
  Hash,
  Rss,
  Bookmark
} from 'lucide-react';

interface Note {
  id: number;
  heading: string;
  body: string;
  userId: number;
  username: string;
  createdAt: string;
  updatedAt: string;
}

const Notes: React.FC = () => {
  const { user } = useAuth();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [viewingNote, setViewingNote] = useState<Note | null>(null);
  const [formData, setFormData] = useState({
    heading: '',
    body: ''
  });

  useEffect(() => {
    loadNotes();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const timeoutId = setTimeout(() => {
        loadNotes();
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      loadNotes();
    }
  }, [searchQuery]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const response = await apiService.getNotes(searchQuery);
      setNotes(response.data);
    } catch (error) {
      console.error('Error loading notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.heading.trim() || !formData.body.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      if (editingNote) {
        await apiService.updateNote(editingNote.id, formData);
        toast.success('Note updated successfully');
      } else {
        await apiService.createNote(formData);
        toast.success('Note created successfully');
      }
      
      setFormData({ heading: '', body: '' });
      setEditingNote(null);
      setShowAddForm(false);
      loadNotes();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error(editingNote ? 'Failed to update note' : 'Failed to create note');
    }
  };

  const handleView = (note: Note) => {
    setViewingNote(note);
  };

  const handleEdit = (note: Note) => {
    setEditingNote(note);
    setFormData({
      heading: note.heading,
      body: note.body
    });
    setShowAddForm(true);
  };

  const handleDelete = async (noteId: number) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await apiService.deleteNote(noteId);
        toast.success('Note deleted successfully');
        loadNotes();
      } catch (error) {
        console.error('Error deleting note:', error);
        toast.error('Failed to delete note');
      }
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const resetForm = () => {
    setFormData({ heading: '', body: '' });
    setEditingNote(null);
    setShowAddForm(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 group">
                <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-teal-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transform group-hover:scale-110 transition-all duration-300">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold gradient-text group-hover:scale-105 transition-transform duration-300">LearnLoop</h1>
              </div>
            </div>
            
            {/* Search Bar */}
            <div className="flex-1 max-w-md mx-8">
              <div className="relative group">
                <input
                  type="text"
                  placeholder="Search notes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-300 hover:border-blue-300 focus:scale-[1.02] shadow-sm hover:shadow-md"
                />
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                <button 
                  className="absolute right-3 top-3 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all duration-300"
                  title="Search by tags"
                >
                  <Hash className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Notes Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
              <div className="mb-6">
                <h2 className="text-3xl font-bold gradient-text mb-2">Your Notes</h2>
                <p className="text-gray-600 text-lg">Organize your thoughts and learning materials</p>
              </div>

              {showAddForm ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Note heading
                    </label>
                    <input
                      type="text"
                      placeholder="Enter note heading here"
                      value={formData.heading}
                      onChange={(e) => setFormData({ ...formData, heading: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-300 focus:scale-[1.02]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Note body
                    </label>
                    <textarea
                      placeholder="Enter note here"
                      value={formData.body}
                      onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                      rows={8}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-300 focus:scale-[1.02] resize-none"
                    />
                  </div>

                  {/* Formatting Toolbar */}
                  <div className="flex items-center space-x-2 p-4 bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl border border-blue-200">
                    <button type="button" className="p-2 hover:bg-blue-100 rounded-lg transition-all duration-300 hover:scale-110">
                      <span className="text-lg">•</span>
                    </button>
                    <button type="button" className="p-2 hover:bg-blue-100 rounded-lg transition-all duration-300 hover:scale-110">
                      <span className="text-lg">1.</span>
                    </button>
                    <button type="button" className="p-2 hover:bg-blue-100 rounded-lg transition-all duration-300 hover:scale-110 font-bold">
                      B
                    </button>
                    <button type="button" className="p-2 hover:bg-blue-100 rounded-lg transition-all duration-300 hover:scale-110 italic">
                      I
                    </button>
                    <button type="button" className="p-2 hover:bg-blue-100 rounded-lg transition-all duration-300 hover:scale-110 underline">
                      U
                    </button>
                  </div>

                  <div className="flex items-center justify-end space-x-3">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-3 text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 transform hover:scale-105"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl hover:from-blue-700 hover:to-teal-700 transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
                    >
                      {editingNote ? 'Update note' : 'Add note'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center py-12">
                  <div className="mx-auto h-20 w-20 bg-gradient-to-r from-blue-100 to-teal-100 rounded-full flex items-center justify-center mb-6 shadow-lg">
                    <Plus className="h-10 w-10 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Start taking notes</h3>
                  <p className="text-gray-600 mb-6 text-lg">Create your first note to get started</p>
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="px-8 py-4 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl hover:from-blue-700 hover:to-teal-700 transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl font-semibold text-lg"
                  >
                    Add New Note
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Notes List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Notes</h3>
              
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-20"></div>
                    </div>
                  ))}
                </div>
              ) : notes.length === 0 ? (
                <div className="text-center py-8">
                  <div className="h-16 w-16 bg-gradient-to-r from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <BookOpen className="h-8 w-8 text-blue-600" />
                  </div>
                  <p className="text-gray-500 text-sm">No notes yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notes.map((note) => (
                    <div key={note.id} className="p-4 border border-blue-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 hover:shadow-md">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-900">
                          {note.heading}
                        </h4>
                        <div className="flex items-center space-x-1 ml-2">
                          <button
                            onClick={() => handleView(note)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-all duration-300 hover:scale-110"
                            title="View note"
                          >
                            <BookOpen className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => handleEdit(note)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-all duration-300 hover:scale-110"
                            title="Edit note"
                          >
                            <Edit className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => handleDelete(note.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-all duration-300 hover:scale-110"
                            title="Delete note"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                        {note.body.length > 100 ? `${note.body.substring(0, 100)}...` : note.body}
                      </p>
                      
                      <p className="text-xs text-gray-500 flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {formatTimeAgo(note.updatedAt)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Note Viewing Modal */}
      {viewingNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl relative">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 bg-gradient-to-r from-blue-600 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {viewingNote.heading}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Created {formatTimeAgo(viewingNote.createdAt)} • Updated {formatTimeAgo(viewingNote.updatedAt)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewingNote(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-300"
              >
                <span className="text-2xl">×</span>
              </button>
            </div>
            
            <div className="mb-6">
              <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl p-6 border border-blue-200">
                <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {viewingNote.body}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setViewingNote(null);
                  handleEdit(viewingNote);
                }}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl hover:from-blue-700 hover:to-teal-700 transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
              >
                Edit Note
              </button>
              <button
                onClick={() => setViewingNote(null)}
                className="px-6 py-3 text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 transform hover:scale-105"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notes;
